import {
  ChevronDown,
  DocumentBlank,
  FlowConnection,
  Help,
  WarningFilled,
} from '@carbon/icons-react';
import * as React from 'react';
import {
  Button,
  IconComponentType,
  LoadingIcon,
  StaticBadge,
  Tooltip,
} from 'vet-bones/design-system/components';

import { ProjectSidebarTileId } from 'src/constants/mosaic';
import { TEST_IDS } from 'src/constants/testIds';
import { EvaluationSummaryIssue } from 'src/constants/voicebox';
import { projectVoiceboxSummaryIssuesSelector } from 'src/selectors/projectVoiceboxSummaryIssues';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';
import {
  useAddDataset,
  useAddQuestions,
  useUnansweredQuestion,
  useUnlinkedConcept,
  useUnusedDataset,
} from 'src/utils/voicebox/hooks/useSummaryActions';

const ActionButton: React.VFC<{
  disabled?: boolean;
  disabledTooltip?: string;
  icon: IconComponentType;
  label: string;
  onClick?: () => Promise<void>;
  testId?: string;
}> = ({ disabled, disabledTooltip, icon, label, onClick, testId }) => (
  <Tooltip
    description={disabledTooltip}
    disabled={!disabled || !disabledTooltip}
    targetProps={{ className: 'sd-voicebox-summary-action' }}
  >
    <Button
      disabled={disabled}
      icon={icon}
      onClick={onClick}
      size="md"
      testId={testId}
      variant="new"
    >
      {label}
    </Button>
  </Tooltip>
);

const UnansweredQuestionsAction: React.VFC<{
  idx: number;
  issue: EvaluationSummaryIssue;
}> = ({ idx, issue }) => {
  const { isDisabled, isLoading, label, onClick } =
    useUnansweredQuestion(issue);
  return (
    <ActionButton
      disabled={isDisabled || isLoading}
      icon={isLoading ? LoadingIcon : Help}
      label={label}
      onClick={onClick}
      testId={TEST_IDS.voicebox.summary.actions.unansweredQuestions(
        issue.target,
        idx
      )}
    />
  );
};

const UnusedResourcesAction: React.VFC<{
  idx: number;
  issue: EvaluationSummaryIssue;
}> = ({ idx, issue }) => {
  const { isDisabled, label, onClick } = useUnusedDataset(issue);
  return (
    <ActionButton
      disabled={isDisabled}
      icon={DocumentBlank}
      label={label}
      onClick={onClick}
      testId={TEST_IDS.voicebox.summary.actions.unusedResources(
        issue.target,
        idx
      )}
    />
  );
};

const UnlinkedConceptAction: React.VFC<{
  idx: number;
  issue: EvaluationSummaryIssue;
}> = ({ idx, issue }) => {
  const { isDisabled, label, onClick } = useUnlinkedConcept(issue);
  return (
    <ActionButton
      disabled={isDisabled}
      icon={FlowConnection}
      label={label}
      onClick={onClick}
      testId={TEST_IDS.voicebox.summary.actions.unlinkedConcepts(
        issue.target,
        idx
      )}
    />
  );
};

const AddQuestionsAction: React.VFC = () => {
  const { isDisabled, onClick } = useAddQuestions();
  return (
    <ActionButton
      disabled={isDisabled}
      icon={Help}
      label={copy.components.voicebox.summary.actions.addQuestions}
      onClick={onClick}
      testId={TEST_IDS.voicebox.summary.actions.addQuestions}
    />
  );
};

const AddDatasetAction: React.VFC = () => {
  const { isDisabled, onClick } = useAddDataset();
  return (
    <ActionButton
      disabled={isDisabled}
      disabledTooltip={
        copy.components.voicebox.disabledFor[ProjectSidebarTileId.RESOURCES]
      }
      icon={DocumentBlank}
      label={copy.components.voicebox.summary.actions.addDataset}
      onClick={onClick}
      testId={TEST_IDS.voicebox.summary.actions.addDataset}
    />
  );
};

const ActionsGroup: React.VFC<{
  badge: string;
  isOpen: boolean;
  issues: React.ReactNode[];
  moreTestId: string;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ badge, isOpen, issues, moreTestId, setIsOpen }) => {
  const firstIssues = issues.slice(0, 3);
  const moreIssues = issues.slice(3);

  if (!firstIssues.length) {
    return null;
  }

  return (
    <div className="sd-voicebox-summary-actions-group">
      <StaticBadge
        leftIcon={WarningFilled}
        size="lg"
        type="error"
        variant="muted-gray"
      >
        {badge}
      </StaticBadge>
      <div className="sd-voicebox-summary-actions-list">
        {firstIssues}
        {moreIssues.length && !isOpen ? (
          <Button
            className="sd-voicebox-summary-actions-more-button"
            leftIcon={ChevronDown}
            onClick={() => setIsOpen(true)}
            size="xs"
            testId={moreTestId}
            variant="tertiary-gray"
          >
            {copy.components.voicebox.summary.actions.more(moreIssues.length)}
          </Button>
        ) : (
          moreIssues
        )}
      </div>
    </div>
  );
};

export const ProjectSidebarVoiceboxSummaryActions: React.VFC = () => {
  const { projectId } = useRouterParams();
  const {
    unansweredQuestions,
    unusedResources,
    unlinkedConcepts,
    addQuestions,
    addDataset,
  } = useReduxSelector(projectVoiceboxSummaryIssuesSelector(projectId));
  const { isIssuesOpen: isOpen, setIsIssuesOpen: setIsOpen } =
    useEvaluateSummary();

  return (
    <>
      <ActionsGroup
        badge={
          copy.components.voicebox.summary.actions.groups.unansweredQuestions
        }
        isOpen={isOpen.unansweredQuestions}
        issues={unansweredQuestions.map((issue, idx) => (
          <UnansweredQuestionsAction
            key={issue.target}
            idx={idx}
            issue={issue}
          />
        ))}
        moreTestId={TEST_IDS.voicebox.summary.more.unansweredQuestions}
        setIsOpen={(nextIsOpen) => setIsOpen('unansweredQuestions', nextIsOpen)}
      />
      <ActionsGroup
        badge={copy.components.voicebox.summary.actions.groups.unusedResources}
        isOpen={isOpen.unusedResources}
        issues={unusedResources.map((issue, idx) => (
          <UnusedResourcesAction key={issue.target} idx={idx} issue={issue} />
        ))}
        moreTestId={TEST_IDS.voicebox.summary.more.unusedResources}
        setIsOpen={(nextIsOpen) => setIsOpen('unusedResources', nextIsOpen)}
      />
      <ActionsGroup
        badge={copy.components.voicebox.summary.actions.groups.unlinkedConcepts}
        isOpen={isOpen.unlinkedConcepts}
        issues={unlinkedConcepts.map((issue, idx) => (
          <UnlinkedConceptAction key={issue.target} idx={idx} issue={issue} />
        ))}
        moreTestId={TEST_IDS.voicebox.summary.more.unlinkedConcepts}
        setIsOpen={(nextIsOpen) => setIsOpen('unlinkedConcepts', nextIsOpen)}
      />
      <ActionsGroup
        badge={copy.components.voicebox.summary.actions.groups.addQuestions}
        isOpen={isOpen.addQuestions}
        issues={addQuestions.map((issue) => (
          <AddQuestionsAction key={issue.target} />
        ))}
        moreTestId={TEST_IDS.voicebox.summary.more.addQuestions}
        setIsOpen={(nextIsOpen) => setIsOpen('addQuestions', nextIsOpen)}
      />
      <ActionsGroup
        badge={copy.components.voicebox.summary.actions.groups.addDataset}
        isOpen={isOpen.addDataset}
        issues={addDataset.map((issue) => (
          <AddDatasetAction key={issue.target} />
        ))}
        moreTestId={TEST_IDS.voicebox.summary.more.addDataset}
        setIsOpen={(nextIsOpen) => setIsOpen('addDataset', nextIsOpen)}
      />
    </>
  );
};
